import * as React from 'react';
import { Fragment } from 'react';
import { compose, setDisplayName, withState, withHandlers, withProps } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import * as moment from 'moment-timezone';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { getNoOffsetCalculator } from 'selectors/serverSelectors';
import { State } from 'reducers';
import { connect } from 'react-redux';
import { UtcTime } from 'types';
import { makeSentenceFragments, FragmentRenderer, renderFragment } from 'util/makeSentenceFragments';
import { Input } from 'ui/shared/Input';
import { Required } from 'ui/pages/designer/Required';

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

type ScheduleRangeEndType = 'never' | 'on' | 'after';

type EndOptionProps = {
    value: ScheduleRangeEndType;
    end: ScheduleRangeEndType;
    handleChangeEnd: ChangeHandler;
};

const EndOption = ({ value, end, handleChangeEnd }: EndOptionProps) =>
    <input
        type="radio"
        id={ `edit-report-schedule-range-end-${value}` }
        value={ value }
        checked={ end === value }
        onChange={ handleChangeEnd }
    />
;

const indentStyle = {
    marginLeft: '2em',
};

type Props = OwnProps & RecomposeProps & TranslateProps;
const RawScheduleRangeEnd = ({ onSentenceFragments, afterSentenceFragments, renderFragment, t, ...props }: Props) =>
    <Fragment>
        <div>
            <label htmlFor="edit-report-schedule-range-end">
                { t('WC_REPORT_SCHEDULE_RANGE_FINISH') }
            </label>
        </div>
        <div style={ indentStyle }>
            <div>
                <label>
                    <EndOption value="never" { ...props }/>
                    { t('2980') }
                </label>
            </div>
            <div>
                <label>
                    <EndOption value="on" { ...props }/>
                    { onSentenceFragments.map(renderFragment) }
                </label>
                <Required visible={ props.end === 'on' && (!props.date || !props.time) } />
            </div>
            <div>
                <label>
                    <EndOption value="after" { ...props }/>
                    { afterSentenceFragments.map(renderFragment) }
                </label>
            </div>
        </div>
    </Fragment>
;

type ChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => void;

type RecomposeProps = {
    onSentenceFragments: string[];
    afterSentenceFragments: string[];
    renderFragment: FragmentRenderer;
    end: ScheduleRangeEndType;
    date: string;
    time: string;
    occurrences: string;
    handleChangeEnd: ChangeHandler;
    handleChangeDate: ChangeHandler;
    handleChangeTime: ChangeHandler;
    handleChangeOccurrences: ChangeHandler;
};
type _RecomposeProps = RecomposeProps & {
    _setEnd: (e: ScheduleRangeEndType) => void;
    _setDate: (s: string) => void;
    _setTime: (s: string) => void;
    _setOccurrences: (s: string) => void;
};
type StateProps = {
    tzOffsetCalculator: (t: UtcTime) => number;
};

const dateNumberToString = (utcTime: UtcTime, tzOffsetCalculator: (t: UtcTime) => number): string =>
    moment.utc(utcTime).utcOffset(-tzOffsetCalculator(utcTime), true).format('YYYY-MM-DD')
;

const timeNumberToString = (utcTime: UtcTime, tzOffsetCalculator: (t: UtcTime) => number): string =>
    moment.utc(utcTime).utcOffset(-tzOffsetCalculator(utcTime), true).format('HH:mm')
;

const update = ({ onChange, date, time, tzOffsetCalculator, occurrences, end }: OwnProps & StateProps & _RecomposeProps) => {
    const utcEnd = moment.utc(`${date} ${time}`, 'YYYY-MM-DD HH:mm');
    if (end === 'never') {
        onChange(reportSettings => {
            if (reportSettings.scheduleRangeEndType === 'never') {
                return reportSettings;
            }
            return reportSettings.setScheduleRangeEnd(ReportSettings.endNever);
        });
    }
    else if (end === 'after') {
        const nOccurrences = parseInt(occurrences, 10);
        onChange(reportSettings => {
            const oldOccurrences = reportSettings.scheduleRangeOccurrences;
            if ((reportSettings.scheduleRangeEndType === 'after' && oldOccurrences === nOccurrences) || isNaN(nOccurrences)) {
                return reportSettings;
            }
            return reportSettings.setScheduleRangeOccurrences(nOccurrences);
        });
    }
    else if (utcEnd.isValid()) {
        const serverEnd = utcEnd.clone().utcOffset(-tzOffsetCalculator(utcEnd.valueOf()));
        onChange(reportSettings => reportSettings.setScheduleRangeEnd(serverEnd.valueOf()));
    }
    else {
        onChange(reportSettings => reportSettings.setScheduleRangeEnd(0));
    }
};

const mapStateToProps = (state: State) => ({
    tzOffsetCalculator: getNoOffsetCalculator(state), // schedules are in local time
});

export const ScheduleRangeEnd = compose<Props, OwnProps>(
    setDisplayName('ScheduleRangeEnd'),
    translate(),
    connect<StateProps, {}, {}>(mapStateToProps),
    withState('end', '_setEnd', ({ reportSettings }: OwnProps & StateProps) => reportSettings.scheduleRangeEndType),
    withState('date', '_setDate', ({ reportSettings, end, tzOffsetCalculator }: OwnProps & StateProps & RecomposeProps) =>
        end === 'on'
            ? dateNumberToString(reportSettings.scheduleRangeEnd, tzOffsetCalculator)
            : ''
    ),
    withState('time', '_setTime', ({ reportSettings, end, tzOffsetCalculator }: OwnProps & StateProps & RecomposeProps) =>
        end === 'on'
            ? timeNumberToString(reportSettings.scheduleRangeEnd, tzOffsetCalculator)
            : ''
    ),
    withState('occurrences', '_setOccurrences', ({ reportSettings, end }: OwnProps & RecomposeProps) =>
        end === 'after'
            ? reportSettings.scheduleRangeOccurrences.toString()
            : ReportSettings.defaultScheduleRangeOccurrences.toString()
    ),
    withHandlers<OwnProps & StateProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleChangeEnd: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.checked) {
                const end = e.target.value as ScheduleRangeEndType;
                props._setEnd(end);
                update({ ...props, end });
            }
        },
        handleChangeDate: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            const date = e.target.value;
            props._setDate(date);
            props._setEnd('on');
            update({ ...props, date, end: 'on' });
        },
        handleChangeTime: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            const time = e.target.value;
            props._setTime(time);
            props._setEnd('on');
            update({ ...props, time, end: 'on' });
        },
        handleChangeOccurrences: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            const occurrences = e.target.value;
            props._setOccurrences(occurrences);
            props._setEnd('after');
            update({ ...props, occurrences, end: 'after' });
        },
    }),
    withProps(({ t, ...props }: RecomposeProps & TranslateProps) => ({
        onSentenceFragments: makeSentenceFragments(t('WC_REPORT_SCHEDULE_RANGE_END_ON_SENTENCE')),
        afterSentenceFragments: makeSentenceFragments(t('WC_REPORT_SCHEDULE_RANGE_END_AFTER_SENTENCE')),
        renderFragment: renderFragment({
            DATE: (
                <Input
                    id="edit-report-schedule-range-end-date"
                    value={ props.date }
                    onChange={ props.handleChangeDate }
                    maxLength={ 10 }
                    placeholder={ t('WC_REPORT_SCHEDULE_DATE_PLACEHOLDER') }
                />
            ),
            TIME: (
                <Input
                    id="edit-report-schedule-range-end-time"
                    value={ props.time }
                    onChange={ props.handleChangeTime }
                    maxLength={ 5 }
                    placeholder={ t('WC_REPORT_SCHEDULE_TIME_PLACEHOLDER') }
                />
            ),
            NUMBER: (
                <Input
                    type="number"
                    id="edit-report-schedule-range-end-occurrences"
                    value={ props.occurrences }
                    onChange={ props.handleChangeOccurrences }
                    min={ 1 }
                    max={ 999 }
                    maxLength={ 3 }
                />
            ),
        }),
    })),
)(RawScheduleRangeEnd);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/schedule/runOnSchedule/ScheduleRangeEnd.tsx